import { createAsyncThunk, createSlice, PayloadAction } from "@reduxjs/toolkit";
import axios from "axios";
import type {
  ByDateItem,
  DistributorProgramSummaryItem,
  DistributorSummaryItem,
  MISVoucherListResponse,
  ProgramSummaryItem,
  UpcomingExpiryItem,
  VoucherStatsItem,
  VoucherStatusSummary,
} from "../../components/MIS/types";

export interface MISFilters {
  fromDate?: string;
  toDate?: string;
  principalIds?: number[];
  programIds?: number[];
  distributorIds?: number[];
  states?: string[];
  minAmount?: number;
  maxAmount?: number;
  search?: string;
  page?: number;
  limit?: number;
  sortBy?: string;
  sortOrder?: "ASC" | "DESC";
}

interface MISPrincipalState {
  programSummary: ProgramSummaryItem[];
  creationByDate: ByDateItem[];
  usageByDate: ByDateItem[];
  expiryByDate: ByDateItem[];
  voucherStats: VoucherStatsItem[];
  upcomingExpiries: UpcomingExpiryItem[];
  statusSummary: VoucherStatusSummary | null;
  voucherList: MISVoucherListResponse | null;
}

interface MISDistributorState {
  distSummary: DistributorSummaryItem[];
  distProgSummary: DistributorProgramSummaryItem[];
  distVoucherStats: VoucherStatsItem[];
  distStatusSummary: VoucherStatusSummary | null;
  distVoucherList: MISVoucherListResponse | null;
}

interface MISState {
  principalLoading: boolean;
  distributorLoading: boolean;
  error: string | null;
  principal: MISPrincipalState;
  distributor: MISDistributorState;
}

const initialState: MISState = {
  principalLoading: false,
  distributorLoading: false,
  error: null,
  principal: {
    programSummary: [],
    creationByDate: [],
    usageByDate: [],
    expiryByDate: [],
    voucherStats: [],
    upcomingExpiries: [],
    statusSummary: null,
    voucherList: null,
  },
  distributor: {
    distSummary: [],
    distProgSummary: [],
    distVoucherStats: [],
    distStatusSummary: null,
    distVoucherList: null,
  },
};

const cleanFilters = (filters: MISFilters) => {
  const cleaned: MISFilters = {};
  Object.entries(filters).forEach(([key, value]) => {
    if (value === undefined || value === null) return;
    if (Array.isArray(value) && value.length === 0) return;
    if (typeof value === "string" && value.trim() === "") return;
    cleaned[key as keyof MISFilters] = value as any;
  });
  return cleaned;
};

const getAuthHeaders = () => {
  const token = localStorage.getItem("accessToken");
  return {
    Authorization: `Bearer ${token}`,
    "Content-Type": "application/json",
  };
};

const postMIS = async (endpoint: string, body: MISFilters) => {
  return axios.request({
    baseURL: `${window.env.API_URL}/v1/utility/admin-mis`,
    url: endpoint,
    method: "POST",
    timeout: 20000,
    headers: getAuthHeaders(),
    data: body,
  });
};

export const fetchPrincipalMIS = createAsyncThunk(
  "mis/fetchPrincipalMIS",
  async (
    {
      filters,
      page = 1,
      limit = 10,
      sortBy = "createdAt",
      sortOrder = "DESC",
    }: {
      filters: MISFilters;
      page?: number;
      limit?: number;
      sortBy?: string;
      sortOrder?: "ASC" | "DESC";
    },
    { rejectWithValue },
  ) => {
    try {
      const baseFilters = cleanFilters(filters);
      const listFilters = cleanFilters({
        ...baseFilters,
        page,
        limit,
        sortBy,
        sortOrder,
      });

      const [
        programSummaryRes,
        creationRes,
        usageRes,
        expiryRes,
        voucherStatsRes,
        upcomingRes,
        statusSummaryRes,
        voucherListRes,
      ] = await Promise.all([
        postMIS("/program-summary", baseFilters),
        postMIS("/voucher-creation-by-date", baseFilters),
        postMIS("/voucher-usage-by-date", baseFilters),
        postMIS("/voucher-expiry-by-date", baseFilters),
        postMIS("/voucher-stats-graph", baseFilters),
        postMIS("/vouchers-expiring-upcoming", baseFilters),
        postMIS("/voucher-status-summary", baseFilters),
        postMIS("/voucher-list", listFilters),
      ]);

      return {
        programSummary: programSummaryRes.data.data || [],
        creationByDate: creationRes.data.data || [],
        usageByDate: usageRes.data.data || [],
        expiryByDate: expiryRes.data.data || [],
        voucherStats: voucherStatsRes.data.data || [],
        upcomingExpiries: upcomingRes.data.data || [],
        statusSummary: statusSummaryRes.data.data || null,
        voucherList: voucherListRes.data || null,
      };
    } catch (error: any) {
      return rejectWithValue(
        error?.response?.data?.message || "Failed to fetch principal MIS data",
      );
    }
  },
);

export const fetchDistributorMIS = createAsyncThunk(
  "mis/fetchDistributorMIS",
  async (
    {
      filters,
      page = 1,
      limit = 10,
      sortBy = "createdAt",
      sortOrder = "DESC",
    }: {
      filters: MISFilters;
      page?: number;
      limit?: number;
      sortBy?: string;
      sortOrder?: "ASC" | "DESC";
    },
    { rejectWithValue },
  ) => {
    try {
      const baseFilters = cleanFilters(filters);
      const listFilters = cleanFilters({
        ...baseFilters,
        page,
        limit,
        sortBy,
        sortOrder,
      });

      const [
        distSummaryRes,
        distProgSummaryRes,
        distStatsRes,
        distStatusRes,
        distListRes,
      ] = await Promise.all([
        postMIS("/distributor-summary", baseFilters),
        postMIS("/distributor-program-summary", baseFilters),
        postMIS("/distributor-voucher-stats-graph", baseFilters),
        postMIS("/distributor-voucher-status-summary", baseFilters),
        postMIS("/distributor-voucher-list", listFilters),
      ]);

      return {
        distSummary: distSummaryRes.data.data || [],
        distProgSummary: distProgSummaryRes.data.data || [],
        distVoucherStats: distStatsRes.data.data || [],
        distStatusSummary: distStatusRes.data.data || null,
        distVoucherList: distListRes.data || null,
      };
    } catch (error: any) {
      return rejectWithValue(
        error?.response?.data?.message || "Failed to fetch distributor MIS data",
      );
    }
  },
);

const MISSlice = createSlice({
  name: "mis",
  initialState,
  reducers: {
    clearMisError(state) {
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchPrincipalMIS.pending, (state) => {
        state.principalLoading = true;
        state.error = null;
      })
      .addCase(
        fetchPrincipalMIS.fulfilled,
        (state, action: PayloadAction<MISPrincipalState>) => {
          state.principalLoading = false;
          state.principal = action.payload;
        },
      )
      .addCase(fetchPrincipalMIS.rejected, (state, action) => {
        state.principalLoading = false;
        state.error = (action.payload as string) || "Failed to load MIS data";
      })
      .addCase(fetchDistributorMIS.pending, (state) => {
        state.distributorLoading = true;
        state.error = null;
      })
      .addCase(
        fetchDistributorMIS.fulfilled,
        (state, action: PayloadAction<MISDistributorState>) => {
          state.distributorLoading = false;
          state.distributor = action.payload;
        },
      )
      .addCase(fetchDistributorMIS.rejected, (state, action) => {
        state.distributorLoading = false;
        state.error = (action.payload as string) || "Failed to load MIS data";
      });
  },
});

export const { clearMisError } = MISSlice.actions;

export default MISSlice.reducer;
